/**
 * Sistema de Quests — tipos + helpers puros (compartidos server + cliente).
 *
 * Una quest tiene N objetivos ORDENADOS (pueden mezclar tipos). Cada objetivo
 * puede ser `blocking`: si lo es, los objetivos posteriores no se "activan"
 * (no cuentan progreso) hasta completarlo. Default: no bloqueante → todos los
 * objetivos corren en paralelo.
 *
 * El catálogo concreto vive en `shared/data/quests.ts`. La lógica de gating por
 * blocking y de completitud es PURA (computeQuestState) para que server (validar)
 * y cliente (render) no la dupliquen. Ver docs/QUESTS.md.
 *
 * Convención del proyecto: IDs internos en inglés, display strings en español.
 */

import type { CharacterClassId, CharacterRaceId } from './character';
import type { FactionId } from '../data/factions';
import type { KingdomId } from '../data/kingdoms';
import { getKingdom } from '../data/kingdoms';

// ============================================================================
// DEFINICIÓN (catálogo)
// ============================================================================

/** Tipo de objetivo. Discriminante de la union `QuestObjective`. */
export type QuestObjectiveType = 'kill' | 'kill_kingdom' | 'collect' | 'talk' | 'reach';

interface QuestObjectiveBase {
  /** Si true, los objetivos posteriores no se activan hasta completar este. Default false. */
  blocking?: boolean;
  /** Texto en español para mostrar (ej. "Matar ratas"). Si falta, se genera uno genérico. */
  descEs?: string;
}

/** Matar N NPCs de un template (`npcId`). */
export interface KillObjective extends QuestObjectiveBase {
  type: 'kill';
  npcId: string;
  count: number;
}

/** Matar N criaturas de un REINO (`kingdom`), sin importar el template concreto. */
export interface KillKingdomObjective extends QuestObjectiveBase {
  type: 'kill_kingdom';
  kingdom: KingdomId;
  count: number;
}

/** Tener/entregar N de un item. Por default se CONSUMEN al entregar la quest. */
export interface CollectObjective extends QuestObjectiveBase {
  type: 'collect';
  itemId: string;
  count: number;
  /** Si false, no se consumen al entregar (solo se pide tenerlos). Default true. */
  consume?: boolean;
}

/** Hablar con un NPC (`npcId` = template id). */
export interface TalkObjective extends QuestObjectiveBase {
  type: 'talk';
  npcId: string;
}

/** Llegar a un área (`areaId` del sistema de áreas). */
export interface ReachObjective extends QuestObjectiveBase {
  type: 'reach';
  areaId: string;
}

export type QuestObjective =
  | KillObjective
  | KillKingdomObjective
  | CollectObjective
  | TalkObjective
  | ReachObjective;

/** Requisitos para que un NPC ofrezca la quest a un personaje. */
export interface QuestRequirements {
  minLevel?: number;
  maxLevel?: number;
  /** IDs de quests que deben estar ENTREGADAS (status completed). */
  requiredQuests?: string[];
  allowedClasses?: CharacterClassId[];
  forbiddenClasses?: CharacterClassId[];
  allowedRaces?: CharacterRaceId[];
  forbiddenRaces?: CharacterRaceId[];
  /** Facciones permitidas (si falta, cualquiera). */
  allowedFactions?: FactionId[];
  /** Honor mínimo requerido. */
  minHonor?: number;
  /**
   * Cantidad mínima de miembros del party (incluyéndose) para poder ACEPTAR la
   * quest y para que sus objetivos CUENTEN. Si el party baja de este número, la
   * quest no se pierde pero deja de avanzar hasta reagruparse. Ver
   * docs/PARTY_SYSTEM.md. Ausente/1 = quest individual.
   */
  minPartyMembers?: number;
}

/** Recompensas otorgadas al ENTREGAR la quest. */
export interface QuestRewards {
  xp?: number;
  gold?: number;
  honor?: number;
  items?: Array<{ itemId: string; quantity: number }>;
  spells?: string[];
  // FUTURO (no implementado): cambio de ascendencia/subclase, XP de skills.
  // ascendancyChange?: string;
  // skillXp?: Array<{ skillId: string; amount: number }>;
}

export interface QuestDefinition {
  /** ID interno en inglés, ej. 'quest_rat_problem'. */
  id: string;
  nameEs: string;
  descriptionEs: string;
  /** Template id del NPC que ofrece la quest. */
  giverNpcId: string;
  /** Template id del NPC donde se entrega. Default: `giverNpcId`. */
  turnInNpcId?: string;
  requirements?: QuestRequirements;
  /** Objetivos ORDENADOS. */
  objectives: QuestObjective[];
  rewards: QuestRewards;
  /** Si true, se puede volver a aceptar tras entregarla. Default false. */
  repeatable?: boolean;
}

// ============================================================================
// ESTADO PERSISTIDO / EN RUNTIME
// ============================================================================

/** Estado de una quest para un personaje. */
export type QuestStatus = 'active' | 'ready' | 'completed';

/** Mapeo status ↔ TINYINT de la columna `character_quests.status`. */
export const QUEST_STATUS_DB: Record<QuestStatus, number> = {
  active: 0,
  ready: 1,
  completed: 2,
};

export function questStatusFromDb(n: number): QuestStatus {
  if (n === 2) return 'completed';
  if (n === 1) return 'ready';
  return 'active';
}

// ============================================================================
// VIEW (shape que viaja al cliente y se rendea en diálogo/log/tracker)
// ============================================================================

export interface QuestObjectiveView {
  type: QuestObjectiveType;
  descEs: string;
  current: number;
  target: number;
  complete: boolean;
  /** Si está contando progreso este frame (no gateado por un blocking previo). */
  active: boolean;
  blocking: boolean;
  /** Referencia concreta del objetivo, para que el cliente muestre su sprite/icono. */
  npcId?: string; // kill / talk
  itemId?: string; // collect
  areaId?: string; // reach
  kingdom?: KingdomId; // kill_kingdom
}

/** Bloqueo por requisito de party (para el tracker: "requiere party de N"). */
export interface QuestPartyGate {
  /** Miembros requeridos. */
  required: number;
  /** Miembros actuales (para "2/4"). */
  current: number;
}

export interface QuestView {
  id: string;
  nameEs: string;
  descriptionEs: string;
  status: QuestStatus;
  objectives: QuestObjectiveView[];
  rewards: QuestRewards;
  /**
   * Presente si la quest requiere un party de N y el personaje no lo cumple: sus
   * objetivos están congelados. El tracker muestra "requiere party de N (current/N)".
   */
  partyGate?: QuestPartyGate;
}

// ============================================================================
// HELPERS PUROS
// ============================================================================

/** Cantidad objetivo de un objetivo (kill/kill_kingdom/collect usan count; talk/reach = 1). */
export function objectiveTarget(obj: QuestObjective): number {
  return obj.type === 'kill' || obj.type === 'kill_kingdom' || obj.type === 'collect'
    ? obj.count
    : 1;
}

/** Label por defecto en español si el objetivo no define `descEs`. */
export function defaultObjectiveLabel(obj: QuestObjective): string {
  switch (obj.type) {
    case 'kill':
      return `Matar ${obj.npcId}`;
    case 'kill_kingdom':
      return `Matar ${getKingdom(obj.kingdom)?.labelEsPlural ?? obj.kingdom}`;
    case 'collect':
      return `Conseguir ${obj.itemId}`;
    case 'talk':
      return `Hablar con ${obj.npcId}`;
    case 'reach':
      return `Llegar a ${obj.areaId}`;
  }
}

export interface ObjectiveProgress {
  current: number;
  target: number;
  complete: boolean;
  active: boolean;
}

/**
 * Aplica el gating por `blocking` y la completitud sobre los valores crudos.
 *
 * `rawCurrents[i]` es el contador crudo del objetivo i (sin clampear), tomado de:
 *  - kill / talk / reach → el counter persistido (talk/reach: 0 ó 1),
 *  - collect → la cantidad actual de ese item en el inventario.
 * El caller (server) arma `rawCurrents`; este helper NO sabe de dónde vienen.
 *
 * Un objetivo i está `active` sólo si todos los objetivos j<i con `blocking:true`
 * están completos. `allComplete` = todos los objetivos completos.
 */
export function computeQuestState(
  def: QuestDefinition,
  rawCurrents: number[]
): { objectives: ObjectiveProgress[]; allComplete: boolean } {
  const objectives: ObjectiveProgress[] = [];
  let gatedByBlocking = false; // true cuando un blocking previo NO está completo

  for (let i = 0; i < def.objectives.length; i++) {
    const obj = def.objectives[i];
    const target = objectiveTarget(obj);
    const current = Math.max(0, Math.min(rawCurrents[i] ?? 0, target));
    const complete = current >= target;
    const active = !gatedByBlocking;

    objectives.push({ current, target, complete, active });

    // Si este objetivo es bloqueante y NO está completo, gatea a los siguientes.
    if (obj.blocking && !complete) {
      gatedByBlocking = true;
    }
  }

  const allComplete = objectives.every((o) => o.complete);
  return { objectives, allComplete };
}

/** Contexto del personaje para validar requisitos (lo arma el server). */
export interface QuestRequirementContext {
  level: number;
  classId: CharacterClassId;
  raceId: CharacterRaceId;
  faction: FactionId | null;
  honor: number;
  /** IDs de quests ya entregadas (status completed). */
  completedQuestIds: ReadonlySet<string>;
  /** Miembros del party del personaje (1 = solo). Para requisitos de party. */
  partySize: number;
}

/** True si el personaje cumple TODOS los requisitos para que se le ofrezca la quest. */
export function meetsQuestRequirements(
  def: QuestDefinition,
  ctx: QuestRequirementContext
): boolean {
  const req = def.requirements;
  if (!req) return true;

  if (req.minLevel !== undefined && ctx.level < req.minLevel) return false;
  if (req.maxLevel !== undefined && ctx.level > req.maxLevel) return false;

  if (req.allowedClasses && !req.allowedClasses.includes(ctx.classId)) return false;
  if (req.forbiddenClasses && req.forbiddenClasses.includes(ctx.classId)) return false;
  if (req.allowedRaces && !req.allowedRaces.includes(ctx.raceId)) return false;
  if (req.forbiddenRaces && req.forbiddenRaces.includes(ctx.raceId)) return false;

  if (req.allowedFactions && (ctx.faction === null || !req.allowedFactions.includes(ctx.faction))) {
    return false;
  }
  if (req.minHonor !== undefined && ctx.honor < req.minHonor) return false;

  if (req.minPartyMembers !== undefined && ctx.partySize < req.minPartyMembers) return false;

  if (req.requiredQuests) {
    for (const q of req.requiredQuests) {
      if (!ctx.completedQuestIds.has(q)) return false;
    }
  }

  return true;
}
